/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { Loader2 } from 'lucide-react';
import React, { useState } from 'react';
import dataProvider from '../../authentication/dataProvider';
import { Button } from '../../components/ui/button';
import { toast } from '../../components/ui/use-toast';
import { UserApprovalData, useUserApproval } from '../../hooks/useUserApproval';
import { ApproveViewButtonsProps } from '../../types/users/approve.view.buttons';

/**
 * BULK USER APPROVAL COMPONENT
 *
 * This component handles bulk approval of multiple users with full blockchain integration.
 *
 * IMPLEMENTATION DETAILS:
 * - Fetches complete user data (wallet addresses, country, etc.) for selected user IDs
 * - Performs blockchain approval for KYC and compliance verification
 * - Updates backend with approval status and transaction hashes
 * - Provides comprehensive error handling and user feedback
 */
const ApproveViewButtons: React.FC<ApproveViewButtonsProps> = ({
  selectedIds,
  resource,
  onSuccess,
  onError,
}) => {
  const { approveUsers, isLoading } = useUserApproval();
  const [fetchingUsers, setFetchingUsers] = useState(false);

  const handleApprove = async () => {
    if (selectedIds.length === 0) {
      toast({
        description: 'Please select at least one user',
        variant: 'destructive',
      });
      return;
    }

    setFetchingUsers(true);

    try {
      // Fetch complete user data for all selected IDs
      console.log(`Fetching user data for ${selectedIds.length} selected users...`);

      const userPromises = selectedIds.map(async (userId) => {
        try {
          const userResponse = await dataProvider.getOne('users', { id: userId });
          if (!userResponse.data) {
            console.warn(`User data not found for ID: ${userId}`);
            return null; // Skip this user instead of failing entirely
          }

          // Validate required fields for blockchain approval
          const user = userResponse.data;
          if (!user.walletAddress) {
            console.warn(`User ${userId} missing wallet address, skipping`);
            return null; // Skip this user instead of failing entirely
          }

          return {
            id: userId,
            walletAddress: user.walletAddress,
            country: user.country || 'Unknown',
            name: user.name || user.email || `User ${userId}`,
          } as UserApprovalData;
        } catch (error) {
          console.error(`Failed to fetch user ${userId}:`, error);
          return null; // Skip this user instead of failing entirely
        }
      });

      const userResults = await Promise.all(userPromises);
      const users = userResults.filter((user): user is UserApprovalData => user !== null);

      console.log(`Successfully fetched data for ${users.length}/${selectedIds.length} users`);

      if (users.length === 0) {
        throw new Error('No valid users found for approval');
      }

      if (users.length < selectedIds.length) {
        console.warn(
          `${selectedIds.length - users.length} user(s) were skipped due to missing data`
        );
      }

      // Perform bulk blockchain approval
      console.log('Starting bulk user approval...');
      const result = await approveUsers(users);

      if (result.success) {
        console.log('Bulk approval completed successfully');
        toast({
          description: `Successfully approved ${users.length} user${users.length > 1 ? 's' : ''}${users.length < selectedIds.length ? ` (${selectedIds.length - users.length} skipped)` : ''}`,
          variant: 'default',
        });
        onSuccess?.();
      } else {
        console.error('Bulk approval failed:', result.error);
        toast({
          description: `Bulk approval failed: ${result.error}`,
          variant: 'destructive',
        });
        onError?.(new Error(result.error));
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to process bulk approval';
      console.error('Bulk approval error:', error);
      toast({
        description: errorMessage,
        variant: 'destructive',
      });
      onError?.(error);
    } finally {
      setFetchingUsers(false);
    }
  };

  const isProcessing = isLoading || fetchingUsers;

  return (
    <Button
      variant='default'
      onClick={handleApprove}
      disabled={isProcessing || selectedIds.length === 0}
      className='flex items-center gap-2'
    >
      {isProcessing ? (
        <>
          <Loader2 className='h-4 w-4 animate-spin' />
          <span>{fetchingUsers ? 'Fetching user data...' : 'Approving...'}</span>
        </>
      ) : (
        <>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='16'
            height='16'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
          >
            <path d='M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2' />
            <circle cx='12' cy='7' r='4' />
          </svg>
          <span>Approve Selected ({selectedIds.length})</span>
        </>
      )}
    </Button>
  );
};

export default ApproveViewButtons;
